import { SearchableSelect, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { closeModal, Modal } from "risa-oasis-ui_v2";
import {
  REASSIGN_MODAL_ASSIGNED_TO_TEXT,
  REASSIGN_MODAL_CANCEL_BUTTON_TEXT,
  REASSIGN_MODAL_NOT_ASSIGNED_TEXT,
  REASSIGN_MODAL_REASSIGN_TO_TEXT,
  REASSIGN_MODAL_SAVE_BUTTON_TEXT,
  REASSIGN_MODAL_SELECT_MEMBER_TEXT,
  REASSIGN_MODAL_TITLE,
  UNASSIGNED_TEXT,
  UNASSIGNED_TEXT_LABEL,
} from "../../../../shared/constants/stringLiterals";
import { ScreenNamesForBigQuery } from "../../../../shared/enums/eventsData";
import { EventsName } from "../../../../shared/enums/eventsName";
import { SingleOrderDetailsTableName } from "../../../../shared/enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { RootState } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import {
  getAssigneeDisplayName,
  insertOrderAssignedReassignedTrailComment,
} from "../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { ToastMessages } from "../../toasts/toastMessages";

/** Props for the {@link ReAssignModal} component. */
interface ReAssignModalProps {
  /** Order ID to reassign. */
  id: string;
  /** Display name of the current assignee. */
  assignedTo?: string;
  /** Provider document ID of the current assignee. */
  assignedToId?: string;
  /** Email of the current assignee. */
  assignedToEmail?: string;
  /** Callback fired after a successful reassignment (e.g. to refresh table data). */
  onComplete?: () => void;
  /** When true, logs an analytics event for the reassignment action. */
  shouldLogEvent?: boolean;
  metaData?: Record<string, unknown>;
}

/**
 * Modal that allows reassigning a medical PA order to a different provider.
 * Uses {@link useUpdateOrderDetails} to persist the assignment change to Postgres.
 */
const ReAssignModal = (props: ReAssignModalProps) => {
  const [providerId, setProviderId] = useState("");
  const { user } = useAuthSelector((state) => state.firebaseAuthentication,
  );
  const { runUpdate, abort, loading } = useUpdateOrderDetails({
    orderId: props.id,
    orgId: getOrgIdForFetchExternalWorklist(),
    tableName: SingleOrderDetailsTableName.MEDICAL_PA_ORDER_GENERAL_INFO,
  });
  const orgId = getOrgIdForFetchExternalWorklist();
  const { data: providersDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const onSelctedOptionChange = (data) => {
    setProviderId(data.value);
  };

  const options = useMemo(() => {
    const providerOptions =
      providersDetails
        ?.map((provider) => {
          return {
            value: provider.id,
            label: generateFullName(
              provider.FirstName || provider.name || "",
              "",
              provider?.LastName || "",
            ),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)) || [];
    return [
      ...providerOptions,
      {
        value: UNASSIGNED_TEXT,
        label: UNASSIGNED_TEXT_LABEL,
      },
    ];
  }, [providersDetails]);

  const assignOrderToProvider = async () => {
    if (props.shouldLogEvent) {
      apiWorkerManager.enqueueAndProcess({
        event_name: EventsName.ASSIGNED_TO_SAVE_CLICKED,
        patient_id: (props.metaData?.patient_id as string) ?? "",
        order_id: props.id,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        org_id: orgId ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.WORKLIST,
          assigned_to: props.assignedToId,
          new_assigned_to: providerId,
          assignee_user_email: (() => {
            const provider = providersDetails?.find((p) => p.id === providerId);
            return provider?.email || provider?.EmailAddresses?.[0] || "";
          })(),
        },
      });
    }
    const providerName = providersDetails?.filter(
      (provider) => provider.id === providerId,
    );

    const payloadForOrderReassignment = {
      assigned_to_id: providerId,
      assigned_to_name: generateFullName(
        providerName?.[0]?.FirstName ||
          providerName?.[0]?.name ||
          UNASSIGNED_TEXT,
        "",
        providerName?.[0]?.LastName || "",
      ),
    };
    const result = await runUpdate(payloadForOrderReassignment);
    if (result.success) {
      const fromAssignee = getAssigneeDisplayName(
        props.assignedToId,
        props.assignedTo,
      );
      const toAssignee = getAssigneeDisplayName(
        providerId,
        payloadForOrderReassignment.assigned_to_name,
      );
      const actingProviderName =
        `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
        "Unknown Provider";

      insertOrderAssignedReassignedTrailComment({
        orderId: props.id,
        orgId: orgId ?? "",
        patientId: (props.metaData?.patient_id as string) ?? undefined,
        providerEmail: user?.email ?? "",
        providerName: actingProviderName,
        fromAssignee,
        toAssignee,
      });

      toast.success(ToastMessages.ASSIGN_ORDER_SUCCESS);
      props.onComplete?.();
    } else {
      logError(
        result.error as Error,
        "Error while assigning order to provider",
      );
      toast.error(ToastMessages.ASSIGN_ORDER_FAILED);
    }
    closeModal(`reAssign-modal-${props.id}`);
  };
  return (
    <>
      <Modal
        dialogId={`reAssign-modal-${props.id}`}
        onSave={assignOrderToProvider}
        title={REASSIGN_MODAL_TITLE}
        saveButtonText={REASSIGN_MODAL_SAVE_BUTTON_TEXT}
        cancelText={REASSIGN_MODAL_CANCEL_BUTTON_TEXT}
        disableSave={providerId === "" || loading}
        onClose={() => abort()}
        onCancel={() => abort()}
      >
        <div className="reassign-modal--layout">
          {props.assignedTo && (
            <div className="reassign-modal--assigned">
              {props.assignedToId === UNASSIGNED_TEXT ? (
                REASSIGN_MODAL_NOT_ASSIGNED_TEXT
              ) : (
                <>
                  {REASSIGN_MODAL_ASSIGNED_TO_TEXT}{" "}
                  <span>{props.assignedTo}</span>
                </>
              )}
            </div>
          )}
          <div className="reassign-modal--reassign-to">
            <SearchableSelect
              id={"reassign-to"}
              onOptionChange={onSelctedOptionChange}
              label={REASSIGN_MODAL_REASSIGN_TO_TEXT}
              placeholder={REASSIGN_MODAL_SELECT_MEMBER_TEXT}
              searchPlaceholder="Search providers"
              value={providerId}
              options={options || []}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReAssignModal;
